import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Download,
  Eye,
  Calendar,
  User,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  History,
  Layers,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { logsService } from '../../services/api';

interface LogChange {
  field: string;
  label: string;
  old: any;
  new: any;
}

interface ActivityLog {
  id: number;
  user: {
    id: number;
    email: string;
    full_name: string;
  } | null;
  user_display: string;
  action_type: string;
  action_type_display: string;
  action_description: string;
  severity: string;
  severity_display: string;
  module: string;
  timestamp: string;
  time_ago: string;
  ip_address?: string;
  user_agent?: string;
  additional_data?: any;
  content_type?: { id: number; app_label: string; model: string } | null;
  object_id?: number | null;
  object_label?: string | null;
  changes: LogChange[];
}

interface FilterOptions {
  search: string;
  module: string;
  action_type: string;
  severity: string;
  start_date: string;
  end_date: string;
  user_id: string;
  content_type: string;
  object_id: string;
  request_id: string;
  only_data: string;
}

const EMPTY_FILTERS: FilterOptions = {
  search: '',
  module: '',
  action_type: '',
  severity: '',
  start_date: '',
  end_date: '',
  user_id: '',
  content_type: '',
  object_id: '',
  request_id: '',
  only_data: ''
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (value === true) return 'Oui';
  if (value === false) return 'Non';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

// Excel exécute une cellule commençant par = + - @ : un nom saisi par un patient
// (formulaire WhatsApp) ne doit pas pouvoir devenir une formule à l'ouverture de l'export.
const csvCell = (value: any): string => {
  const text = String(value ?? '');
  return `"${(/^[=+\-@]/.test(text) ? `'${text}` : text).replace(/"/g, '""')}"`;
};

const changesTitle = (log: ActivityLog): string => {
  if (log.action_type === 'delete') return 'Contenu supprimé';
  if (log.action_type === 'create') return 'Valeurs enregistrées';
  return 'Modifications';
};

const ActivityLogsList: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [relatedLabel, setRelatedLabel] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filtres
  const [filters, setFilters] = useState<FilterOptions>(EMPTY_FILTERS);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        page_size: 25
      };

      // Ajouter les filtres non vides
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        }
      });

      const response = await logsService.getActivities(params);
      
      setLogs(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setTotalPages(Math.ceil((response.data.count || 0) / 25));
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des logs d\'activité');
      console.error('Erreur logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset à la première page lors du filtrage
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  // Tout ce qu'une même requête a écrit (cascade de suppression, écritures liées), ou la vie d'un enregistrement
  const showRelated = (related: Partial<FilterOptions>, label: string) => {
    setFilters({ ...EMPTY_FILTERS, ...related });
    setRelatedLabel(label);
    setCurrentPage(1);
    setShowDetail(false);
  };

  const handleExport = async () => {
    try {
      const params: any = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        }
      });

      const response = await logsService.exportActivities(params);
      
      // Créer et télécharger le fichier CSV
      const csvContent = convertToCSV(response.data.data);
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `activity_logs_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;
      link.click();
    } catch (err) {
      console.error('Erreur export:', err);
    }
  };

  const convertToCSV = (data: ActivityLog[]): string => {
    const headers = ['Date', 'Utilisateur', 'Action', 'Description', 'Module', 'Sévérité', 'IP', 'Changements'];
    const rows = data.map(log => [
      format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss'),
      log.user_display,
      log.action_type_display,
      log.action_description,
      log.module,
      log.severity_display,
      log.ip_address || '',
      (log.changes || []).map(c => `${c.label} : ${formatValue(c.old)} → ${formatValue(c.new)}`).join(' | ')
    ]);

    return [headers, ...rows]
      .map(row => row.map(csvCell).join(','))
      .join('\n');
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'success': return 'text-green-800 bg-green-100';
      case 'info': return 'text-blue-800 bg-blue-100';
      case 'warning': return 'text-yellow-800 bg-yellow-100';
      case 'error': return 'text-red-800 bg-red-100';
      case 'critical': return 'text-purple-800 bg-purple-100';
      default: return 'text-gray-800 bg-gray-100';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'success': return <CheckCircle className="h-4 w-4" />;
      case 'info': return <Activity className="h-4 w-4" />;
      case 'warning': return <AlertCircle className="h-4 w-4" />;
      case 'error': return <AlertCircle className="h-4 w-4" />;
      case 'critical': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const viewLogDetail = async (log: ActivityLog) => {
    try {
      const response = await logsService.getActivityDetail(log.id);
      setSelectedLog(response.data);
      setShowDetail(true);
    } catch (err) {
      console.error('Erreur détail log:', err);
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header et contrôles */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Logs d'Activité</h2>
            <p className="mt-1 text-sm text-gray-500">
              {totalCount.toLocaleString()} activités au total
            </p>
          </div>

          <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center text-sm text-gray-700 mr-2">
              <input
                type="checkbox"
                checked={filters.only_data === 'true'}
                onChange={(e) => handleFilterChange('only_data', e.target.checked ? 'true' : '')}
                className="mr-2 text-gray-700"
              />
              Données modifiées uniquement
            </label>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${showFilters ? 'bg-gray-100' : ''}`}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtres
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </button>
          </div>
        </div>

        {(filters.request_id || filters.object_id) && (
          <div className="mt-4 flex items-center justify-between bg-blue-50 text-blue-800 text-sm px-3 py-2 rounded-lg">
            <span>{relatedLabel}</span>
            <button onClick={resetFilters} aria-label="Retirer ce filtre" className="ml-3 hover:text-blue-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Filtres */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recherche
                </label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10 block w-full border-gray-200 rounded-xl shadow-sm text-sm text-gray-700"
                    placeholder="Rechercher..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Module
                </label>
                <select
                  value={filters.module}
                  onChange={(e) => handleFilterChange('module', e.target.value)}
                  className="block w-full border-gray-200 rounded-xl shadow-sm text-sm text-gray-700"
                >
                  <option value="">Tous les modules</option>
                  <option value="patients">Patients</option>
                  <option value="consultations">Consultations</option>
                  <option value="facturation">Facturation</option>
                  <option value="planning">Planning</option>
                  <option value="stock">Stock</option>
                  <option value="caisse">Caisse</option>
                  <option value="tresorerie">Trésorerie</option>
                  <option value="comptabilite">Comptabilité</option>
                  <option value="depenses">Dépenses</option>
                  <option value="prosthesis">Prothèses</option>
                  <option value="treatments">Soins</option>
                  <option value="appointments">WhatsApp</option>
                  <option value="administration">Administration</option>
                  <option value="authentication">Authentification</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'action
                </label>
                <select
                  value={filters.action_type}
                  onChange={(e) => handleFilterChange('action_type', e.target.value)}
                  className="block w-full border-gray-200 rounded-xl shadow-sm text-sm text-gray-700"
                >
                  <option value="">Toutes les actions</option>
                  <option value="create">Création</option>
                  <option value="update">Modification</option>
                  <option value="delete">Suppression</option>
                  <option value="read">Consultation</option>
                  <option value="login">Connexion</option>
                  <option value="logout">Déconnexion</option>
                  <option value="export">Export / impression</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sévérité
                </label>
                <select
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                  className="block w-full border-gray-200 rounded-xl shadow-sm text-sm text-gray-700"
                >
                  <option value="">Toutes les sévérités</option>
                  <option value="success">Succès</option>
                  <option value="info">Information</option>
                  <option value="warning">Avertissement</option>
                  <option value="error">Erreur</option>
                  <option value="critical">Critique</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de début
                </label>
                <input
                  type="datetime-local"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="block w-full border-gray-200 rounded-xl shadow-sm text-sm text-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de fin
                </label>
                <input
                  type="datetime-local"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="block w-full border-gray-200 rounded-xl shadow-sm text-sm text-gray-700"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={resetFilters}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Réinitialiser
              </button>
              <button
                onClick={fetchLogs}
                className="px-4 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700"
              >
                Appliquer
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Liste des logs */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="divide-y divide-gray-200">
          {logs.map((log) => (
            <div key={log.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(log.severity)}`}>
                    {getSeverityIcon(log.severity)}
                    <span className="ml-1">{log.severity_display}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {log.action_description}
                      </p>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {log.module}
                      </span>
                    </div>
                    
                    {log.action_type === 'update' && log.changes?.length > 0 && (
                      <p className="mt-1 text-xs text-gray-600 truncate">
                        Modifié : {log.changes.map(c => capitalize(c.label)).join(', ')}
                      </p>
                    )}

                    <div className="flex items-center space-x-4 mt-1">
                      <div className="flex items-center text-xs text-gray-500">
                        <User className="h-3 w-3 mr-1" />
                        {log.user_display}
                      </div>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="h-3 w-3 mr-1" />
                        {log.time_ago}
                      </div>

                      {log.ip_address && (
                        <div className="text-xs text-gray-500">
                          IP: {log.ip_address}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => viewLogDetail(log)}
                  aria-label="Voir le détail"
                  className="ml-4 text-gray-400 hover:text-gray-600"
                >
                  <Eye className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {logs.length === 0 && !loading && (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Aucun log trouvé
            </h3>
            <p className="text-sm text-gray-500">
              Essayez de modifier vos critères de recherche
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Affichage de {((currentPage - 1) * 25) + 1} à {Math.min(currentPage * 25, totalCount)} sur {totalCount} résultats
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <span className="text-sm text-gray-700">
                  Page {currentPage} sur {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de détail */}
      {showDetail && selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Détail de l'activité #{selectedLog.id}
              </h3>
              <button
                onClick={() => setShowDetail(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Utilisateur</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.user_display}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date et heure</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {format(new Date(selectedLog.timestamp), 'dd/MM/yyyy à HH:mm:ss', { locale: fr })}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <p className="mt-1 text-sm text-gray-900">{selectedLog.action_description}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Module</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.module}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type d'action</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.action_type_display}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sévérité</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(selectedLog.severity)}`}>
                    {selectedLog.severity_display}
                  </span>
                </div>
              </div>

              {selectedLog.ip_address && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Adresse IP</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.ip_address}</p>
                </div>
              )}

              {selectedLog.changes?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{changesTitle(selectedLog)}</label>
                  <div className="overflow-auto max-h-80 border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs text-gray-500">
                        <tr>
                          <th scope="col" className="px-3 py-2 font-medium">Champ</th>
                          {selectedLog.action_type !== 'create' && (
                            <th scope="col" className="px-3 py-2 font-medium">
                              {selectedLog.action_type === 'delete' ? 'Valeur' : 'Avant'}
                            </th>
                          )}
                          {selectedLog.action_type !== 'delete' && (
                            <th scope="col" className="px-3 py-2 font-medium">
                              {selectedLog.action_type === 'create' ? 'Valeur' : 'Après'}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedLog.changes.map((change) => (
                          <tr key={change.field}>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap align-top">{capitalize(change.label)}</td>
                            {selectedLog.action_type !== 'create' && (
                              <td className={`px-3 py-2 align-top break-words ${selectedLog.action_type === 'update' ? 'text-red-700' : 'text-gray-900'}`}>
                                {formatValue(change.old)}
                              </td>
                            )}
                            {selectedLog.action_type !== 'delete' && (
                              <td className={`px-3 py-2 align-top break-words ${selectedLog.action_type === 'update' ? 'text-green-700' : 'text-gray-900'}`}>
                                {formatValue(change.new)}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedLog.additional_data?.path && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requête</label>
                  <p className="mt-1 text-sm text-gray-900 break-all">
                    {selectedLog.additional_data.method} {selectedLog.additional_data.path}
                  </p>
                </div>
              )}

              {selectedLog.user_agent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Navigateur / appareil</label>
                  <p className="mt-1 text-xs text-gray-600 break-all">{selectedLog.user_agent}</p>
                </div>
              )}

              {selectedLog.additional_data && Object.keys(selectedLog.additional_data).length > 0 && (
                <details>
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">Données techniques</summary>
                  <pre className="mt-1 text-sm text-gray-900 bg-gray-100 p-3 rounded overflow-auto max-h-40">
                    {JSON.stringify(selectedLog.additional_data, null, 2)}
                  </pre>
                </details>
              )}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              {selectedLog.content_type && selectedLog.object_id && (
                <button
                  onClick={() => showRelated(
                    { content_type: String(selectedLog.content_type!.id), object_id: String(selectedLog.object_id) },
                    `Historique : ${selectedLog.object_label} #${selectedLog.object_id}`
                  )}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded hover:bg-gray-50"
                >
                  <History className="h-4 w-4 mr-2" />
                  Historique de cet enregistrement
                </button>
              )}
              {selectedLog.additional_data?.request_id && (
                <button
                  onClick={() => showRelated(
                    { request_id: selectedLog.additional_data.request_id },
                    `Toutes les écritures de l'opération : ${selectedLog.action_description}`
                  )}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded hover:bg-gray-50"
                >
                  <Layers className="h-4 w-4 mr-2" />
                  Toute l'opération
                </button>
              )}
              <button
                onClick={() => setShowDetail(false)}
                className="px-4 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogsList;